import clsx from 'clsx';
import React, { CSSProperties, ReactNode, useEffect, useState } from 'react';

import styles from './ButtonGroup.module.scss';

interface CustomCssProperties extends CSSProperties {
  '--custom-active-bg': string;
}

export interface ButtonGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  /** Content for each button in the group; the array length determines the number of buttons. */
  labels: ReactNode[];
  /** Index of the initially-active (and externally-controlled) button. Defaults to `0`. */
  activeIndex?: number;
  /** Called with the clicked index. The group also tracks the active index internally and updates on click. */
  onClick?: (index: number) => void;
  /** CSS color (any valid CSS color string) applied to the active button's background. Falls back to the design token when omitted. */
  customActiveBackgroundColor?: string;
  /** Indices of buttons that should be rendered as disabled and ignore clicks. */
  disabledIndices?: number[];
}

/**
 * Segmented control: a row of mutually-exclusive buttons where exactly
 * one is active at a time. Use for compact toggles between two or more
 * sibling options (e.g. tabs, view modes, time ranges).
 *
 * Active state is tracked internally but stays in sync with
 * `activeIndex` whenever the prop changes, so it can be driven either
 * way.
 */
export const ButtonGroup = ({
  labels,
  activeIndex = 0,
  onClick,
  customActiveBackgroundColor,
  className,
  disabledIndices = [],
  ...rest
}: ButtonGroupProps) => {
  const [index, setIndex] = useState(activeIndex);

  useEffect(() => {
    setIndex(activeIndex);
  }, [activeIndex]);

  const handleClick = (clickIndex: number) => () => {
    setIndex(clickIndex);
    onClick?.(clickIndex);
  };

  const activeStyle: CustomCssProperties | undefined =
    customActiveBackgroundColor
      ? { '--custom-active-bg': customActiveBackgroundColor }
      : undefined;

  return (
    <div
      className={clsx(styles.root, className)}
      role="group"
      data-button-group
      {...rest}
    >
      {labels.map((label, _index) => {
        const isActive = index === _index;
        const isDisabled = disabledIndices.includes(_index);

        return (
          <button
            key={_index}
            type="button"
            data-testid={`button-group-item-${_index}`}
            className={clsx(styles.item, {
              [styles.itemActive]: isActive,
              [styles.itemDisabled]: isDisabled,
            })}
            data-button-group-item
            onClick={handleClick(_index)}
            data-is-active={isActive}
            style={isActive ? activeStyle : undefined}
            disabled={isDisabled}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};
